
import React from 'react';

export const FinancialTermsFinalCTA: React.FC = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyValue: '',
    timeline: '',
    questions: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qoadc98t1oshnre0d0', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyValue: '',
          timeline: '',
          questions: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-20 h-20 bg-teal-400 rounded-full"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-blue-400 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-indigo-400 rounded-full"></div>
        <div className="absolute bottom-40 right-10 w-24 h-24 bg-purple-400 rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Ready to Maximize Your 
            <span className="text-teal-600 block mt-2">Apartment Building Sale?</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
            Understanding financial terms is just the beginning. Get personalized guidance to optimize your sale price, 
            minimize transaction costs, and maximize your net proceeds.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calculator-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Net Proceeds Analysis</h3>
              <p className="text-gray-600 text-sm">Calculate your true net earnings after all costs and taxes</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Market Positioning</h3>
              <p className="text-gray-600 text-sm">Strategic pricing and timing for maximum returns</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-exchange-line text-2xl text-indigo-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Optimization</h3>
              <p className="text-gray-600 text-sm">1031 exchanges and other wealth preservation strategies</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-white/20 backdrop-blur-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Get Your FREE Financial Analysis</h3>
              
              <form id="financial-terms-consultation" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address
                  </label>
                  <input
                    type="text"
                    id="propertyAddress"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                    placeholder="Property street address"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="propertyValue" className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Property Value
                    </label>
                    <select
                      id="propertyValue"
                      name="propertyValue"
                      value={formData.propertyValue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select value range</option>
                      <option value="Under $500K">Under $500K</option>
                      <option value="$500K - $1M">$500K - $1M</option>
                      <option value="$1M - $2M">$1M - $2M</option>
                      <option value="$2M - $5M">$2M - $5M</option>
                      <option value="$5M - $10M">$5M - $10M</option>
                      <option value="Over $10M">Over $10M</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                      Selling Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select timeline</option>
                      <option value="ASAP">ASAP</option>
                      <option value="Within 3 months">Within 3 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6-12 months">6-12 months</option>
                      <option value="Over 1 year">Over 1 year</option>
                      <option value="Just exploring">Just exploring</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="questions" className="block text-sm font-medium text-gray-700 mb-2">
                    Questions or Specific Concerns
                  </label>
                  <textarea
                    id="questions"
                    name="questions"
                    value={formData.questions}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Tell us about your specific situation, goals, or any questions about the financial terms covered in this guide..."
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.questions.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white font-bold py-4 px-8 rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending Request...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-line mr-2"></i>
                      Get My FREE Analysis
                    </span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                      <span className="text-sm">Thank you! We'll contact you within 24 hours to discuss your financial analysis.</span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <span className="text-sm">Please check all required fields and try again.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Work With Financial Experts?</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-money-dollar-circle-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Maximize Net Proceeds</h4>
                    <p className="text-gray-600 text-sm">
                      Our expertise in transaction cost negotiation and tax strategies typically saves clients 
                      3-8% of their sale price - often $50,000-200,000 on typical apartment buildings.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-shield-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Risk Mitigation</h4>
                    <p className="text-gray-600 text-sm">
                      Navigate complex regulations, avoid costly mistakes, and ensure all legal and financial 
                      requirements are properly handled throughout the transaction process.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-time-line text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Faster Closings</h4>
                    <p className="text-gray-600 text-sm">
                      Professional guidance accelerates the process, reduces delays, and ensures smooth 
                      closings that protect your interests and timeline requirements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-lightbulb-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Strategic Insights</h4>
                    <p className="text-gray-600 text-sm">
                      Market timing advice, 1031 exchange opportunities, and portfolio optimization strategies 
                      that enhance your long-term wealth building and investment goals.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-8 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">🔥 Limited Time: FREE Analysis Worth $2,500</h3>
              <p className="text-gray-300 mb-6">
                We're offering complimentary financial analysis for apartment building owners who submit their 
                information this month. This comprehensive review normally costs $2,500 but is free for qualified properties.
              </p>
              <div className="flex items-center text-yellow-300">
                <i className="ri-time-line mr-2"></i>
                <span className="text-sm">Response within 24 hours • No obligation • 100% confidential</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
